import {
  addStarredWorkspace,
  deleteWorkspace,
  fetchOrganization,
  fetchStarredWorkspace,
  unstarWorkspace,
} from '../../features/organization/organizationSlice';
import { getWorkspaceById, preloadDocumentTemplate } from '../../utils/common';
import { Notification } from '../../components/Notification';
import { useDispatch, useSelector } from 'react-redux';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Button } from 'antd';

import {
  DELETE_MODAL,
  WORKSPACE_SETTINGS_MODAL,
  WORKSPACE_IMPORT_MODAL,
  WORKSPACE_CATEGORY_CHANGE_MODAL,
} from '../../constants/common';
import Leftbar from '../../components/Leftbar';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { StarFilled, StarOutlined } from '@ant-design/icons';
const WorkspaceSettings = lazy(() => import('./WorkspaceSettings'));
const CategoryModal = lazy(() => import('../../components/Modal/CategoryModal'));
const CreateWorkspaceModal = lazy(() =>
  import('../../components/CreateWorkspaceModal').then((module) => ({ default: module.CreateWorkspaceModal }))
);
const DeleteModal = lazy(() => import('../../components/Modal').then((module) => ({ default: module.DeleteModal })));
const BottomNav = lazy(() => import('../../components/MobileFriendly/BottomNav'));

const PrivateLayout = ({ children, routeName }) => {
  const location = useLocation();

  const { userOrganizationId } = useSelector((state) => state.user);
  const { newWorkspaceList } = useSelector((state) => state.organization);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [openCategoryChangeModal, setOpenCategoryChangeModal] = useState(false);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState('');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeStaticMenu, setActiveStaticMenu] = useState('');
  const [openWorkspaceImport, setOpenWorkspaceImport] = useState(false);

  useEffect(() => {
    if (routeName === 'home') {
      preloadDocumentTemplate('/img/documentTemplate.webp');
    }
  }, [routeName]);

  useEffect(() => {
    const workspaceId = location.pathname.split('/')[2] || '';
    setActiveStaticMenu(location.pathname.split('/')[1] || '');
    if (workspaceId) {
      setActiveWorkspaceId(workspaceId);
    }
  }, [location.pathname]);

  const openModal = useCallback(
    (workspaceId, modalType) => {
      if (modalType === DELETE_MODAL) {
        setOpenDeleteModal(true);
        setActiveWorkspaceId(workspaceId);
      }
      if (modalType === WORKSPACE_SETTINGS_MODAL) {
        setOpenSettingsModal(true);
        setActiveWorkspaceId(workspaceId);
      }
      if (modalType === WORKSPACE_CATEGORY_CHANGE_MODAL) {
        setOpenCategoryChangeModal(true);

        setActiveWorkspaceId(workspaceId);
      }
    },
    [setOpenDeleteModal, setActiveWorkspaceId, setOpenSettingsModal, setOpenCategoryChangeModal]
  );

  const closeModal = useCallback(
    (modalType) => {
      if (modalType === DELETE_MODAL) {
        setOpenDeleteModal(false);
        setActiveWorkspaceId('');
      }
      if (modalType === WORKSPACE_SETTINGS_MODAL) {
        setOpenSettingsModal(false);
        setActiveWorkspaceId('');
      }
      if (modalType === WORKSPACE_IMPORT_MODAL) {
        setOpenWorkspaceImport(false);
      }
      if (modalType === WORKSPACE_CATEGORY_CHANGE_MODAL) {
        setOpenCategoryChangeModal(false);
      }
    },
    [setOpenDeleteModal, setActiveWorkspaceId, setOpenSettingsModal, setOpenWorkspaceImport, setOpenCategoryChangeModal]
  );

  useEffect(() => {
    dispatch(fetchOrganization(userOrganizationId));
  }, [userOrganizationId]);

  useEffect(() => {
    dispatch(fetchStarredWorkspace({}));
  }, []);

  const deleteClickHandler = useCallback(
    async (workspaceId) => {
      const params = {
        organization_id: userOrganizationId,
      };
      dispatch(deleteWorkspace({ workspaceId, params })).then(({ payload }) => {
        if (payload) {
          navigate('/home', { replace: true });
        }
        closeModal(DELETE_MODAL);
      });
    },
    [userOrganizationId, dispatch, navigate, closeModal]
  );

  const handleStarWorkspace = useCallback(
    async (workspace_id, isStarred) => {
      if (isStarred) {
        dispatch(unstarWorkspace({ id: workspace_id })).then(() => dispatch(fetchStarredWorkspace({})));
      } else {
        dispatch(addStarredWorkspace({ id: workspace_id })).then(() => dispatch(fetchStarredWorkspace({})));
      }
    },
    [dispatch]
  );

  const popoverMenuContent = useCallback(
    (isStarred) => [
      {
        id: 'delete',
        element: (
          <Button type="button" size="small">
            Delete
          </Button>
        ),
        clickHandler: (id) => {
          openModal(id, DELETE_MODAL);
        },
      },
      {
        id: 'settings',
        element: (
          <Button type="button" size="small">
            Settings
          </Button>
        ),
        clickHandler: (id) => {
          openModal(id, WORKSPACE_SETTINGS_MODAL);
        },
      },
      {
        id: 'move-workspaces',
        element: (
          <Button type="button" size="small">
            Move Workspace
          </Button>
        ),
        clickHandler: (id) => {
          const workspace = getWorkspaceById(id, newWorkspaceList);

          if (workspace?.type === 'PRIVATE') {
            Notification.error('Private workspaces cannot be moved');
            return;
          }
          if (workspace?.is_git_based) {
            Notification.error('BitBucket based workspaces cannot be moved');
            return;
          } else {
            openModal(id, WORKSPACE_CATEGORY_CHANGE_MODAL);
          }
        },
      },
      {
        id: 'star',
        element: isStarred ? (
          <>
            <StarFilled style={{ fontSize: 16, color: '#ffab00' }} /> Remove from favourite
          </>
        ) : (
          <>
            <StarOutlined style={{ fontSize: 16, color: '#ffab00' }} /> Add to favorites
          </>
        ),
        clickHandler: (id) => handleStarWorkspace(id, isStarred),
      },
    ],
    [openModal, newWorkspaceList, handleStarWorkspace]
  );

  const deleteModalSubmitHandler = useCallback(() => {
    deleteClickHandler(activeWorkspaceId);
  }, [activeWorkspaceId, deleteClickHandler]);

  return (
    <>
      <div className="home-layout">
        {openWorkspaceImport && (
          <Suspense fallback={null}>
            <CreateWorkspaceModal open={openWorkspaceImport} onCancel={() => closeModal(WORKSPACE_IMPORT_MODAL)} />
          </Suspense>
        )}
        {openSettingsModal && (
          <Suspense fallback={null}>
            <WorkspaceSettings
              open={openSettingsModal}
              afterClose={() => {
                closeModal(WORKSPACE_SETTINGS_MODAL);
              }}
              workspace_id={activeWorkspaceId}
            />
          </Suspense>
        )}
        {openDeleteModal && (
          <Suspense fallback={null}>
            <DeleteModal
              open={openDeleteModal}
              onCancel={() => {
                closeModal(DELETE_MODAL);
              }}
              onSubmit={deleteModalSubmitHandler}
            />
          </Suspense>
        )}
        {openCategoryChangeModal && (
          <Suspense fallback={null}>
            <CategoryModal
              open={openCategoryChangeModal}
              id={activeWorkspaceId}
              onCancel={() => {
                closeModal(WORKSPACE_CATEGORY_CHANGE_MODAL);
              }}
            />
          </Suspense>
        )}

        {routeName !== 'chat' && !isMobile && (
          <Leftbar
            activeStaticMenu={activeStaticMenu}
            popoverMenuContent={popoverMenuContent}
            routeName={routeName}
            setOpenWorkspaceCreateModal={setOpenWorkspaceImport}
          />
        )}

        <div className="home-layout-inner" style={{ width: '100%', overflowX: 'hidden' }}>
          {children}
        </div>
      </div>
      {isMobile && (
        <Suspense fallback={null}>
          <BottomNav />
        </Suspense>
      )}
    </>
  );
};

export default PrivateLayout;
